import {FC, useEffect, useState} from 'react'
import {api, BedTypeCatalog, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {PageHead} from '../../components/PageHead'
import {ModalPortal} from '../../components/ModalPortal'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const TONE: Record<string, string> = {TWIN: '#039540', FULL: '#7c4dff', QUEEN: '#0aa37f'}

/**
 * The bed-type price catalog. Each room rents one bed type; this is the single
 * place to set the monthly price per type — it drives rooms, beds, bookings,
 * and the flyer.
 */
const BedTypesPage: FC = () => {
  const toast = useToast()
  const [types, setTypes] = useState<BedTypeCatalog[]>([])
  const [loading, setLoading] = useState(true)
  const [edit, setEdit] = useState<BedTypeCatalog | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      setTypes(await api.bedTypes())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <PageHead icon='🛏️' eyebrow='Operations' title='Bed Types'
        sub='Set the monthly price per bed type. Every room rents one type and inherits its price.' />

      {loading ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
      ) : (
        <div className='row g-3 ccp-stagger'>
          {types.map((t) => (
            <div className='col-12 col-md-6 col-lg-4' key={t.id}>
              <div className='ccp-lift' style={{background: '#fff', borderRadius: 14, padding: '20px 22px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', height: '100%', borderTop: `4px solid ${TONE[t.key] ?? ACCENT}`}}>
                <div style={{fontSize: 13, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em'}}>{t.key}</div>
                <div style={{fontSize: 16, fontWeight: 700, color: INK, margin: '2px 0 8px'}}>{t.label}</div>
                <div style={{fontSize: 30, fontWeight: 800, color: ACCENT}}>${Number(t.pricePerMonth).toFixed(0)}<span style={{fontSize: 13, fontWeight: 600, color: MUTED}}>/month</span></div>
                <button onClick={() => setEdit(t)} style={{...btnGhost, marginTop: 16, width: '100%'}}>Edit price</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <ModalPortal><EditModal
          type={edit}
          onClose={() => setEdit(null)}
          onSaved={(saved) => {
            setTypes((l) => l.map((x) => (x.id === saved.id ? saved : x)))
            setEdit(null)
            toast.success(`${saved.label} updated.`)
          }}
        /></ModalPortal>
      )}
    </div>
  )
}

const EditModal: FC<{type: BedTypeCatalog; onClose: () => void; onSaved: (t: BedTypeCatalog) => void}> = ({type, onClose, onSaved}) => {
  const [label, setLabel] = useState(type.label)
  const [price, setPrice] = useState(Number(type.pricePerMonth).toFixed(2))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setErr(null)
    if (!label.trim()) return setErr('Label is required.')
    if (!/^\d+(\.\d{1,2})?$/.test(price)) return setErr('Enter a valid monthly price (e.g. 375.00).')
    setBusy(true)
    try {
      onSaved(await api.updateBedType(type.id, {label: label.trim(), pricePerMonth: price}))
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Edit {type.key} beds</h2>
        <p style={{color: MUTED, fontSize: 14, margin: '0 0 18px'}}>This price applies to every bed of this type, everywhere.</p>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}
        <Field label='Label'><input value={label} onChange={(e) => setLabel(e.target.value)} style={inp} /></Field>
        <Field label='Monthly price ($)'><input value={price} onChange={(e) => setPrice(e.target.value)} placeholder='375.00' style={inp} /></Field>
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
          <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button onClick={save} style={{...btnPrimary, minWidth: 110}} disabled={busy}>{busy ? <Spinner size={14} /> : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{marginBottom: 14}}>
    <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)

const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '9px 14px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

export {BedTypesPage}
